import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Clock, Loader2, LogOut, User } from 'lucide-react';
import { endImpersonation } from '../lib/admin';
import { supabase } from '../lib/supabase';

interface ImpersonationInfo {
  impersonationId: string;
  supportToken: string;
  targetUser: {
    id: string;
    username: string;
  };
  expiresAt: string;
}

const STORAGE_KEY = 'adminImpersonation';
const LEGACY_ADMIN_SESSION_STORAGE_KEY = 'adminSupportAdminSession';

function AdminImpersonationBanner() {
  const [impersonationInfo, setImpersonationInfo] =
    useState<ImpersonationInfo | null>(null);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [ending, setEnding] = useState(false);
  const [exitError, setExitError] = useState<string | null>(null);

  useEffect(() => {
    // Remove credentials left by earlier support-session builds, if present.
    sessionStorage.removeItem(LEGACY_ADMIN_SESSION_STORAGE_KEY);
    const storedInfo = localStorage.getItem(STORAGE_KEY);
    if (!storedInfo) return;

    try {
      const info = JSON.parse(storedInfo) as ImpersonationInfo;
      if (
        !info.impersonationId ||
        !info.supportToken ||
        !info.targetUser?.id ||
        !info.expiresAt
      ) {
        throw new Error('Incomplete support session');
      }
      setImpersonationInfo(info);
    } catch (error) {
      console.error('Error reading support session:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const finishSession = useCallback(async (
    outcome: 'ended' | 'expired' = 'ended'
  ) => {
    if (!impersonationInfo || ending) return;

    setEnding(true);
    setExitError(null);

    try {
      await endImpersonation(
        impersonationInfo.impersonationId,
        impersonationInfo.supportToken,
        outcome
      );
    } catch (error) {
      console.error('Error ending support session:', error);
      // The local account must still be exited even if the audit request fails.
    }

    try {
      const { error: signOutError } = await supabase.auth.signOut({
        scope: 'local',
      });
      if (signOutError) throw signOutError;

      localStorage.removeItem(STORAGE_KEY);
      window.location.replace('/login');
    } catch (error) {
      console.error('Error leaving temporary user account:', error);
      setExitError('Could not exit this account. Please try again.');
      setEnding(false);
    }
  }, [ending, impersonationInfo]);

  useEffect(() => {
    if (!impersonationInfo) return;

    const updateTimeRemaining = () => {
      const expiresAt = new Date(impersonationInfo.expiresAt).getTime();
      const difference = expiresAt - Date.now();

      if (difference <= 0) {
        setTimeRemaining('0:00');
        void finishSession('expired');
        return;
      }

      const minutes = Math.floor(difference / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimeRemaining();
    const interval = window.setInterval(updateTimeRemaining, 1000);
    return () => window.clearInterval(interval);
  }, [finishSession, impersonationInfo]);

  if (!impersonationInfo) return null;

  return (
    <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div>
                <span className="font-bold text-sm">ADMIN SUPPORT ACCESS ACTIVE</span>
                <div className="flex items-center text-xs text-yellow-100">
                  <User className="h-3 w-3 mr-1" />
                  Viewing as: @{impersonationInfo.targetUser.username}
                </div>
              </div>
              <div className="flex items-center text-xs text-yellow-100">
                <Clock className="h-3 w-3 mr-1" />
                Expires in: {timeRemaining}
              </div>
            </div>
          </div>

          {exitError && <p role="alert" className="text-sm font-medium">{exitError}</p>}
          <button
            onClick={() => void finishSession('ended')}
            disabled={ending}
            className="self-start sm:self-auto bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {ending ? (
              <>
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                Ending...
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4 mr-2" />
                Exit support access
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminImpersonationBanner;
